import { gsap } from 'gsap'
import { CSSRulePlugin } from 'gsap/CSSRulePlugin'
import { GSDevTools } from 'gsap/GSDevTools'
import axios from 'axios'

const lib = require('../library')
const prod = process.env.NODE_ENV === 'production'

gsap.registerPlugin(CSSRulePlugin, GSDevTools)

export default class Overlay {
  constructor(el) {
    this.el = el
    this.setVars()
    this.init()
  }

  // Set initial variables for Overlay class
  setVars() {
    this.body = document.querySelector('body')
    this.trigger = this.el
    this.allTriggers = document.querySelectorAll('[data-module="Overlay"]')
    this.url = this.trigger.dataset.overlayUrl
    this.ease = 'Power1.easeInOut'
    this.resultContainer = document.querySelector('[data-overlay-target]')
    this.replace = this.resultContainer.querySelector('[data-replace]')
    this.screen = this.resultContainer.querySelector('[data-overlay-screen]')
    this.loadingClass = this.el.dataset.loadingClass ?? 's-loading'
    this.fromOverlay = this.trigger.hasAttribute('data-overlay-link')
  }

  // Initialize the Overlay class by adding an event listener to the trigger
  init() {
    this.trigger.addEventListener('click', this.handleResultsLoad)
  }

  // Add the loading class to the element
  addLoadingClass() {
    this.el.classList.add(this.loadingClass)
  }

  // Remove the loading class from the element
  removeLoadingClass() {
    this.el.classList.remove(this.loadingClass)
  }

  // Handle the loading of results by making an AJAX request to load content
  handleResultsLoad = (e) => {
    e.preventDefault()

    // Check if the trigger is inside a Carousel module and if
    // it has a dragging class, bail on the handleResultsLoad function
    const carousel = this.el.closest('[data-module="Carousel"]')
    if (carousel && carousel.classList.contains('is-dragging')) {
      return
    }

    if (this.trigger.classList.contains('s-disabled')) {
      return
    }

    this.allTriggers.forEach((trigger) => {
      if (trigger.classList.contains('s-disabled')) {
        trigger.classList.remove('s-disabled')
      }
    })

    // Check if the click is from within the overlay
    if (this.fromOverlay && this.checkState === 'opened') {
      // Updated to use this.fromOverlay
      this.url = this.trigger.dataset.overlayUrl // Update the URL from the new trigger
      this.replaceContent()
    } else {
      this.preScreen('in')
      lib.buttonStates(this.trigger, 'loading')

      this.addLoadingClass()
      axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
      axios
        .get(this.url)
        .then(({ data }) => {
          this.appendResults(data)
          lib.reInitModules('AlgoliaSearch')
          lib.reInitModules('ProductsNav')
          // lib.reInitModules('Overlay');
          this.removeLoadingClass() // Remove loading class in success case
        })
        .catch((error) => {
          lib.buttonStates(this.trigger, 'error')
          this.removeLoadingClass() // Remove loading class in error case
          if (!prod) {
            lib.logError('Overlay failed to AJAX load', error)
          }
        })
    }
  }

  // Get the Overlay instance and its related elements after loading
  getOverlayInstance() {
    this.overlay = this.resultContainer.querySelectorAll('[data-overlay]')
    this.overlayBody = this.resultContainer.querySelector('[data-overlay-body]')
    this.close = this.resultContainer.querySelectorAll('[data-close-overlay]')
    this.close.forEach((closeButton) => this.handleClose(closeButton))
  }

  // Append the loaded content to the result container
  appendResults = (body) => {
    this.replace.innerHTML = body

    // Re-initialize Overlay module for any new overlay triggers within the loaded content
    const newTriggers = this.replace.querySelectorAll('[data-module="Overlay"]')
    initOverlayModule(newTriggers)

    this.animateIn()
  }

  // Handle the closing of the Overlay by adding an event listener to the close button
  handleClose(button) {
    button.addEventListener('click', this.closeStack)
    window.addEventListener('keydown', this.closeStackOnEscape)
  }

  handleKeydown = (event) => {
    if (event.key === 'Escape') {
      this.closeStack()
    }
  }

  // Close the Overlay by animating it out
  closeStack = () => {
    this.preScreen('out')
    this.animateOut(this)
  }

  closeStackOnEscape = (event) => {
    if (event.key === 'Escape') {
      this.closeStack()
    }
  }
  // Check the current state of the Overlay (opened or closed)
  checkState() {
    if (this.body.classList.contains('s-overlay-open')) {
      return 'opened'
    }
    return 'closed'
  }

  // Pre-screen animation for the Overlay (state: 'in' or 'out')
  preScreen(state = 'in') {
    const pre = gsap.timeline()
    if (state === 'in') {
      this.screen.classList.remove('hidden')
      pre.to(this.screen, {
        opacity: 1,
      })
    } else {
      pre.to(this.screen, {
        opacity: 0,
      })

      pre.eventCallback('onComplete', () => {
        this.screen.classList.add('hidden')
      })
    }
  }

  // Animate the Overlay in
  animateIn() {
    this.getOverlayInstance()
    this.resultContainer.classList.remove('hidden')
    const tlOverlay = gsap.timeline({
      defaults: {
        duration: 0.1,
        opacity: 0,
        ease: this.ease,
      },
    })

    // If already open, simply animate to next body area
    if (this.checkState() === 'opened') {
      tlOverlay.from(
        this.overlayBody,
        {
          opacity: 0,
          x: this.fromOverlay ? 0 : '-80', // Conditionally apply the -y axis animation
          ease: this.fromOverlay ? 'none' : 'back',
        },
        '<',
      )
    }

    // otherwise, animate the entire overlay in
    else {
      this.body.classList.add('s-overlay-open')
      tlOverlay.from(this.overlayBody, {
        opacity: 0,
        y: this.fromOverlay ? 0 : '-150',
        ease: this.fromOverlay ? 'none' : 'back',
        duration: 0.3,
      })
    }
    lib.handleBodyScroll('disabled')
    lib.handleBodyScroll('disabled')
    lib.buttonStates(this.trigger, 'disabled')
  }

  // Animate the Overlay out
  animateOut() {
    // Let's quickly destroy the texture if it exists
    const texture = this.resultContainer.querySelector(
      '[data-moduleDefer="GridBg"]',
    )

    const tlOverlayOut = gsap.timeline({
      defaults: {
        duration: 0.35,
        opacity: 0,
        ease: this.ease,
      },
    })
    // Stats
    if (typeof texture !== 'undefined' && texture !== null) {
      texture.remove()
    }

    tlOverlayOut
      .to(this.overlayBody, {
        opacity: 0,
        y: this.fromOverlay ? 0 : '-150',
      })
      .to(
        this.overlay,
        {
          opacity: 0,
          yPercent: this.fromOverlay ? 0 : '-60',
        },
        '-=.3',
      )

    tlOverlayOut.eventCallback('onComplete', this.destroyStack)
  }

  // if the user clicks an overlay link from another overlay
  replaceContent() {
    lib.buttonStates(this.trigger, 'loading')
    this.addLoadingClass()
    axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest'
    axios
      .get(this.url)
      .then(({ data }) => {
        this.replace.innerHTML = data

        // Re-initialize Overlay module for any new overlay triggers within the loaded content
        const newTriggers = this.replace.querySelectorAll(
          '[data-module="Overlay"]',
        )
        // eslint-disable-next-line no-use-before-define
        initOverlayModule(newTriggers)

        lib.reInitModules('AlgoliaSearch')
        lib.reInitModules('ProductsNav')
        this.removeLoadingClass() // Remove loading class in success case
      })
      .catch((error) => {
        lib.buttonStates(this.trigger, 'error')
        this.removeLoadingClass() // Remove loading class in error case
        if (!prod) {
          lib.logError('Overlay failed to AJAX load', error)
        }
      })
  }

  // Destroy the Overlay stack after animating out
  destroyStack = () => {
    this.body.classList.remove('s-overlay-open')
    lib.handleBodyScroll('enabled')
    this.resultContainer.replace = ''
    this.resultContainer.classList.add('hidden')
    lib.buttonStates(this.trigger, 'ready')
    window.removeEventListener('keydown', this.closeStackOnEscape)
  }
}

/*
Helper function to support loading an overlay from a link within the overlay
 */
function initOverlayModule(elements) {
  elements.forEach((el) => {
    new Overlay(el)
  })
}
